import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import {
  initialValues,
  registerValidationSchema,
  RegisterValues,
} from "../../validation/RegisterValidation";
import { AuthService } from "../../services/authService";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";
import RegisterStep1 from "../../components/features/registerForm/RegisterStep1";
import { DocumentService } from "../../services/documentService";
import { Country } from "../../models/MonetaryZones";
import RegisterStep0 from "../../components/features/registerForm/RegisterStep0";
import RegisterStep2 from "../../components/features/registerForm/RegisterStep2";

const RegisterPage: React.FC = () => {
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const stepperRef = useRef(null);
  const toastRef = useRef<Toast>(null);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: registerValidationSchema,
    onSubmit: async (values: RegisterValues) => {
      try {
        setSubmitting(true);
        const res = await AuthService.registerUser(values);
        if (res.status === -1) {
          toastRef.current?.show({
            severity: "warn",
            summary: "Registration Failed",
            detail: res.message,
          });
          return;
        }
        toastRef.current?.show({
          severity: "success",
          summary: "Registration Successful",
          detail: res.message,
        });
        navigate("/auth");
      } catch (err: any) {
        const error =
          err.response?.data?.message || "An error occurred. Please try again.";
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: error,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleNext = () => {
    setCurrentStep(currentStep + 1);
  };
  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const fetchCountries = async () => {
    const res = await DocumentService.getAllCountries();
    setCountries(res.data);
  };
  useEffect(() => {
    fetchCountries();
  }, []);

  return (
    <>
      <Toast ref={toastRef} position="top-center" />
      <div className="flex flex-col items-center mt-5">
        <h4 className="text-xl mb-1 font-bold">Sign Up to your Account</h4>
        <p className="mb-6 text-slate-500 text-sm">
          Welcome back! Please enter your details.
        </p>
      </div>
      <Stepper
        activeStep={currentStep}
        ref={stepperRef}
        headerPosition="top"
        linear
      >
        {/* Step 1: User Type */}
        <StepperPanel header="User type">
          <RegisterStep0 formik={formik} />
          <div className="flex pt-4 justify-end"></div>
        </StepperPanel>

        {/* Step 2: Personal Details */}
        <StepperPanel header="Personal details">
          <RegisterStep1 formik={formik} appCountries={countries} />
        </StepperPanel>

        {/* Step 3: Company Details (Conditional) */}
        {formik.values.isEntreprise && (
          <StepperPanel header="Company details">
            <RegisterStep2 formik={formik} appCountries={countries} />
          </StepperPanel>
        )}
      </Stepper>

      {/* Footer */}
      <div className="mt-4 text-center text-sm">
        <div className="flex items-start mb-4">
          <input
            type="checkbox"
            id="accepted"
            name="accepted"
            onChange={(e) => setAccepted(!!e.target.checked)}
            className="p-2 h-5 w-5 me-2 "
          />
          {/* <Checkbox
            inputId="accepted"
            name="accepted"
            variant="filled"
            icon="pi pi-box"
            checked={accepted}
            onChange={(e) => setAccepted(!!e.checked)}
            className=" me-2 rounded-sm border border-slate-300 bg-slate-300"
          /> */}
          <label htmlFor="accepted">
            By signing in you agree to the{" "}
            <Link to={"/"} className="text-primary-600">
              Terms and conditions of our platform
            </Link>
          </label>
        </div>
        <div className="flex mb-4 justify-end md:justify-center gap-4">
          {currentStep === 0 ? (
            <Button
              label="Next"
              // icon="pi pi-arrow-right"
              className="btn-primary"
              onClick={handleNext}
            />
          ) : currentStep === 1 ? (
            <>
              <Button
                label="Back"
                // icon="pi pi-arrow-left"
                className="text-secondary-800"
                onClick={handleBack}
              />
              {formik.values.isEntreprise ? (
                <Button
                  label="Next"
                  // icon="pi pi-arrow-right"
                  className="btn-primary"
                  onClick={handleNext}
                />
              ) : (
                <Button
                  disabled={!formik.isValid || submitting || !accepted}
                  label="Sign Up"
                  loading={submitting}
                  className="btn-primary "
                  onClick={() => formik.handleSubmit()}
                />
              )}
            </>
          ) : currentStep === 2 ? (
            <>
              <Button
                label="Back"
                // icon="pi pi-arrow-left"
                className="text-secondary-800"
                onClick={handleBack}
              />
              <Button
                disabled={!formik.isValid || submitting || !accepted}
                label="Sign Up"
                loading={submitting}
                className="btn-primary "
                onClick={() => formik.handleSubmit()}
              />
            </>
          ) : (
            ""
          )}
        </div>
        <p>
          Already have an account?{" "}
          <Link to={"/auth"} className="text-primary-600">
            Sign In
          </Link>
        </p>
      </div>
    </>
  );
};

export default RegisterPage;
